import { observer } from "mobx-react";
import { Checkbox, Alert, Form, Divider } from "antd";

interface MobileApplicationStepProps {
  data: {
    clientSettings: {
      allowManifestTakeover: boolean;
      franchiseePreInspection: boolean;
      jobPreInspection: boolean;
      jobPostInspection: boolean;
      franchiseePostInspection: boolean;
    };
    csRequestManifestEnabled: boolean;
    csRequestManifestChangeRequestDateEnabled: boolean;
    csAdHocVehicleInspectionEnabled: boolean;
    csContactsEnabled: boolean;
    csRouteManifestForceCompanyDriveExecuteActivitiesInSequence: boolean;
    csRouteManifestForceFranchiseeDriveExecuteActivitiesInSequence: boolean;
    csMultiTripRequestNextTripEnabled: boolean;
    csStopRouteEnabled: boolean;
    csActivityStopCancelJobBeforeArrivalEnabled: boolean;
    csActivityStopReAttemptCancelledJobsEnabled: boolean;
    csActivityStopSuspendOptionVisible: boolean;
    csGroupedJobsEnabled: boolean;
    csGroupedJobsRestrictGroupingOnlyToLoadedItems: boolean;
    csGroupedJobsRestrictGroupingOnlyToJobsOnTheSameNode: boolean;
    csVisitPlanJobItemOperationPartialQuantityChangeEnabled: boolean;
    csVisitPlanJobItemOperationPartialStatusEnabled: boolean;
    csVehicleLoadingBarcodeScanningIncreasesPartialQuantityLoaded: boolean;
    csAdHocDeliveryEnabled: boolean;
    csAdHocCollectionEnabled: boolean;
    csAdHocJobOpenVisitPlanAfterCreateCollection: boolean;
    csVisitPlanAdHocItemCreationEnabled: boolean;
    csAdHocJobItemSetOperationStatusToSuccessWhenCreate: boolean;
    csAdHocJobItemQuantityVisible: boolean;
  };
  modules: {
    mobileApp: boolean;
  };
  generalSettings: {
    enableNodeAggregation: boolean;
  };
  onChange: (data: any) => void;
}

const MobileApplicationStep = observer(({ data, modules, generalSettings, onChange }: MobileApplicationStepProps) => {
  if (!modules.mobileApp) {
    return (
      <div className="p-4">
        <Alert message="Mobile Application Not Selected" description="Mobile Application configuration is only applicable when Mobile Application module is selected." type="info" showIcon />
      </div>
    );
  }

  const handleChange = (field: string, value: any) => {
    // Check if this field belongs in clientSettings
    const clientSettingsFields = ["allowManifestTakeover", "franchiseePreInspection", "jobPreInspection", "jobPostInspection", "franchiseePostInspection"];
    const isClientSettingsField = clientSettingsFields.includes(field);

    let updates: any = {};

    if (isClientSettingsField) {
      updates.clientSettings = { ...data.clientSettings, [field]: value };
    } else {
      updates[field] = value;
    }

    // Auto-set related fields based on business rules
    if (field === "csAdHocVehicleInspectionEnabled") {
      updates.clientSettings = { ...data.clientSettings, jobPreInspection: value };
      if (!value) {
        updates.clientSettings.franchiseePreInspection = false;
      }
    }

    if (field === "jobPostInspection" && !value) {
      updates.clientSettings = { ...updates.clientSettings, franchiseePostInspection: false };
    }

    if (field === "csGroupedJobsEnabled") {
      if (value) {
        updates.csGroupedJobsRestrictGroupingOnlyToLoadedItems = true;
        // Set to true by default if enableNodeAggregation is enabled
        if (generalSettings.enableNodeAggregation) {
          updates.csGroupedJobsRestrictGroupingOnlyToJobsOnTheSameNode = true;
        }
      }
    }

    if (field === "csAdHocCollectionEnabled" && value) {
      updates.csAdHocJobOpenVisitPlanAfterCreateCollection = true;
    }

    if (field === "csVisitPlanAdHocItemCreationEnabled") {
      if (value) {
        updates.csAdHocJobItemSetOperationStatusToSuccessWhenCreate = true;
      } else {
        updates.csAdHocJobItemQuantityVisible = false;
      }
    }

    if (field === "csVisitPlanJobItemOperationPartialQuantityChangeEnabled") {
      const partialQtyEnabled = field === "csVisitPlanJobItemOperationPartialQuantityChangeEnabled" ? value : data.csVisitPlanJobItemOperationPartialQuantityChangeEnabled;

      updates.csVisitPlanJobItemOperationPartialStatusEnabled = partialQtyEnabled;
      updates.csVehicleLoadingBarcodeScanningIncreasesPartialQuantityLoaded = partialQtyEnabled;
    }

    // Force both company and franchisee sequence settings to be the same
    if (field === "csRouteManifestForceCompanyDriveExecuteActivitiesInSequence") {
      updates.csRouteManifestForceFranchiseeDriveExecuteActivitiesInSequence = value;
    }

    onChange({ ...data, ...updates });
  };

  return (
    <div className="p-4">
      <Alert message="Mobile Application Configuration" description="Configure settings specific to the Mobile Application." type="info" showIcon className="mb-6" />

      <Form layout="vertical">
        {/* Manifest Management */}
        <Divider orientation="left">Manifest Management</Divider>
        <Form.Item className="mb-4">
          <Checkbox checked={data.csRequestManifestEnabled} onChange={(e) => handleChange("csRequestManifestEnabled", e.target.checked)}>
            Allow mobile user to request manifest?
          </Checkbox>
        </Form.Item>

        <Form.Item className="mb-4">
          <Checkbox checked={data.csRequestManifestChangeRequestDateEnabled} onChange={(e) => handleChange("csRequestManifestChangeRequestDateEnabled", e.target.checked)}>
            Allow user to request future dated manifests?
          </Checkbox>
        </Form.Item>

        <Form.Item className="mb-4">
          <Checkbox checked={data.clientSettings.allowManifestTakeover} onChange={(e) => handleChange("allowManifestTakeover", e.target.checked)}>
            Allow mobile user to take over manifest from application?
          </Checkbox>
        </Form.Item>

        {/* Vehicle Inspections */}
        <Divider orientation="left">Vehicle Inspections</Divider>
        <Form.Item className="mb-4">
          <div className="space-y-2">
            <Checkbox checked={data.csAdHocVehicleInspectionEnabled} onChange={(e) => handleChange("csAdHocVehicleInspectionEnabled", e.target.checked)}>
              Enable Vehicle Pre-Departure Inspection?
            </Checkbox>
            <div className="ml-6">
              <Checkbox
                checked={data.clientSettings.franchiseePreInspection}
                onChange={(e) => handleChange("franchiseePreInspection", e.target.checked)}
                disabled={!data.csAdHocVehicleInspectionEnabled}
              >
                Enable also for Subcontracted Routes?
              </Checkbox>
            </div>
          </div>
        </Form.Item>

        <Form.Item className="mb-4">
          <div className="space-y-2">
            <Checkbox checked={data.clientSettings.jobPostInspection} onChange={(e) => handleChange("jobPostInspection", e.target.checked)}>
              Enable Vehicle Post-Return Inspection?
            </Checkbox>
            <div className="ml-6">
              <Checkbox
                checked={data.clientSettings.franchiseePostInspection}
                onChange={(e) => handleChange("franchiseePostInspection", e.target.checked)}
                disabled={!data.clientSettings.jobPostInspection}
              >
                Enable also for Subcontracted Routes?
              </Checkbox>
            </div>
          </div>
        </Form.Item>

        {/* Other Functionality */}
        <Divider orientation="left">Other Functionality</Divider>
        <Form.Item className="mb-4">
          <Checkbox checked={data.csContactsEnabled} onChange={(e) => handleChange("csContactsEnabled", e.target.checked)}>
            Contacts
          </Checkbox>
        </Form.Item>

        <Form.Item className="mb-4">
          <Checkbox
            checked={data.csRouteManifestForceCompanyDriveExecuteActivitiesInSequence}
            onChange={(e) => handleChange("csRouteManifestForceCompanyDriveExecuteActivitiesInSequence", e.target.checked)}
          >
            Force driver to follow manifest sequence?
          </Checkbox>
        </Form.Item>

        <Form.Item className="mb-4">
          <Checkbox checked={data.csMultiTripRequestNextTripEnabled} onChange={(e) => handleChange("csMultiTripRequestNextTripEnabled", e.target.checked)}>
            Allow driver to request next trip from application?
          </Checkbox>
        </Form.Item>

        <Form.Item className="mb-4">
          <Checkbox checked={data.csStopRouteEnabled} onChange={(e) => handleChange("csStopRouteEnabled", e.target.checked)}>
            Allow driver to terminate the route prematurely?
          </Checkbox>
        </Form.Item>

        <Form.Item className="mb-4">
          <Checkbox checked={data.csActivityStopCancelJobBeforeArrivalEnabled} onChange={(e) => handleChange("csActivityStopCancelJobBeforeArrivalEnabled", e.target.checked)}>
            Cancel Job Before Arrival
          </Checkbox>
        </Form.Item>

        <Form.Item className="mb-4">
          <Checkbox checked={data.csActivityStopReAttemptCancelledJobsEnabled} onChange={(e) => handleChange("csActivityStopReAttemptCancelledJobsEnabled", e.target.checked)}>
            Re-attempt Cancelled Jobs
          </Checkbox>
        </Form.Item>

        <Form.Item className="mb-4">
          <Checkbox checked={data.csActivityStopSuspendOptionVisible} onChange={(e) => handleChange("csActivityStopSuspendOptionVisible", e.target.checked)}>
            Suspend Job
          </Checkbox>
        </Form.Item>

        <Form.Item className="mb-4">
          <div className="space-y-2">
            <Checkbox checked={data.csGroupedJobsEnabled} onChange={(e) => handleChange("csGroupedJobsEnabled", e.target.checked)}>
              Job Grouping
            </Checkbox>
            {data.csGroupedJobsEnabled && (
              <div className="ml-6">
                <Checkbox checked={data.csGroupedJobsRestrictGroupingOnlyToJobsOnTheSameNode} onChange={(e) => handleChange("csGroupedJobsRestrictGroupingOnlyToJobsOnTheSameNode", e.target.checked)}>
                  Limit grouping to jobs in the same node?
                </Checkbox>
              </div>
            )}
          </div>
        </Form.Item>

        <Form.Item className="mb-4">
          <Checkbox
            checked={data.csVisitPlanJobItemOperationPartialQuantityChangeEnabled && data.csVisitPlanJobItemOperationPartialStatusEnabled}
            onChange={(e) => {
              handleChange("csVisitPlanJobItemOperationPartialQuantityChangeEnabled", e.target.checked);
              //handleChange("csVisitPlanJobItemOperationPartialStatusEnabled", e.target.checked);
            }}
          >
            Enable partial quantities on visit plan?
          </Checkbox>
        </Form.Item>

        {/* Ad-hoc Job Creation */}
        <Divider orientation="left">Enable ad-hoc job creation for</Divider>
        <Form.Item className="mb-4">
          <Checkbox checked={data.csAdHocDeliveryEnabled} onChange={(e) => handleChange("csAdHocDeliveryEnabled", e.target.checked)}>
            Deliveries?
          </Checkbox>
        </Form.Item>

        <Form.Item className="mb-4">
          <Checkbox checked={data.csAdHocCollectionEnabled} onChange={(e) => handleChange("csAdHocCollectionEnabled", e.target.checked)}>
            Collections?
          </Checkbox>
        </Form.Item>

        <Form.Item className="mb-4">
          <div className="space-y-2">
            <Checkbox checked={data.csVisitPlanAdHocItemCreationEnabled} onChange={(e) => handleChange("csVisitPlanAdHocItemCreationEnabled", e.target.checked)}>
              Enable ad-hoc item creation?
            </Checkbox>
            {data.csVisitPlanAdHocItemCreationEnabled && (
              <div className="ml-6">
                <Checkbox checked={data.csAdHocJobItemQuantityVisible} onChange={(e) => handleChange("csAdHocJobItemQuantityVisible", e.target.checked)}>
                  Set Item Quantity?
                </Checkbox>
              </div>
            )}
          </div>
        </Form.Item>
      </Form>
    </div>
  );
});

export default MobileApplicationStep;
